import React, { useState, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlus, faTrash, faCircleCheck, faCircleXmark,
  faMagnifyingGlass, faXmark, faEye, faGlobe,
} from '@fortawesome/free-solid-svg-icons';
import { Checkbox } from '@mui/material';
import { useTranslation } from 'react-i18next';
import UploadFileModal from './uploadFileModal.js';
import ProductAnalyzerModal from './ProductAnalyzerModal.js';
import ProductsPreviewModal from './ProductsPreviewModal.js';
import AddProductWebsiteModal from './AddProductWebsiteModal.js';
import ConfirmationModal from './confirmationModal.js';
import { guardWrite } from '../utils/permissions';
import '../translations.js';
import './knowledgeOverview.css';

// Products subsection of the Knowledge Base. Kept as its own component (rather
// than another branch in the ~3500-LOC knowledgeOverview) so the product upload
// + list + delete flow stays readable. A "product catalog" is a knowledge_data
// row of type='product' (one uploaded CSV/JSON file); the individual products
// live in the backend `products` table and the bot's `{bot_id}__products`
// Pinecone namespace. Upload, list, and delete all reuse the existing knowledge
// endpoints (ingest_products + the shared `knowledge` DELETE).
const ProductsKnowledge = ({ bot, products = [], onRefresh, isAtKnowledgeLimit, onUpgrade, remainingSlots = Infinity, hideWebsiteSource = false }) => {
  const { t } = useTranslation();
  const [uploadOpen, setUploadOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [pendingDeleteIds, setPendingDeleteIds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // Product Analyzer (analyze → confirm → ingest) state.
  const [analyzeOpen, setAnalyzeOpen] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [drafts, setDrafts] = useState(null);
  const [pendingUrls, setPendingUrls] = useState([]);
  // Add-from-website flow.
  const [websiteOpen, setWebsiteOpen] = useState(false);
  const [websiteUrl, setWebsiteUrl] = useState(null);
  // Preview modal (View a catalog's products, paginated).
  const [previewItem, setPreviewItem] = useState(null);

  const API = process.env.REACT_APP_API_BASE_URL;

  // Hide auto-synced store catalogs (Wix/Shopify/...) from the manual list — they
  // are managed from their own source card on the Products page, not uploaded here.
  // The sync tags them with a url_link of 'product-source:<key>'.
  const manualProducts = useMemo(
    () => (products || []).filter(p => !String(p.url_link || '').startsWith('product-source:')),
    [products]
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = q
      ? manualProducts.filter(p => (p.document_name || p.url_link || '').toLowerCase().includes(q))
      : manualProducts;
    return [...list].sort((a, b) => (a.document_name || '').localeCompare(b.document_name || ''));
  }, [manualProducts, search]);

  const allSelected = filtered.length > 0 && filtered.every(p => selectedIds.includes(p.id));
  // Base the indeterminate state on the VISIBLE rows, so a search that hides all
  // selected rows doesn't leave the header checkbox stuck showing a dash.
  const someSelected = filtered.some(p => selectedIds.includes(p.id)) && !allSelected;

  const toggleSelected = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };
  const toggleSelectAll = () => {
    setSelectedIds(allSelected ? [] : filtered.map(p => p.id));
  };

  // Step 1: files uploaded → run the Product Analyzer, then open the confirm
  // modal. The analyzer profiles each file and drafts a schema (summary,
  // category, per-column descriptions) for the owner to review before import.
  const handleFilesUploaded = async (fileUrls) => {
    if (!guardWrite()) return;
    setError(null);
    setWebsiteUrl(null);   // a file flow must never route to the website endpoint
    setPendingUrls(fileUrls);
    setDrafts(null);
    setAnalyzing(true);
    setAnalyzeOpen(true);
    try {
      const response = await fetch(`${API}analyze_products`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ bot_id: bot.id, product_urls: fileUrls }),
      });
      if (!response.ok) {
        if (response.status === 403) { setAnalyzeOpen(false); return; }
        throw new Error(`analyze failed (${response.status})`);
      }
      const data = await response.json();
      setDrafts(data.catalogs || []);
    } catch (err) {
      // Analysis is an enhancement, not a gate: if it fails, fall back to a
      // plain import (the backend still derives a category from the URL path).
      console.error('Product analysis failed, importing without confirmation:', err);
      setAnalyzeOpen(false);
      await ingestProducts(fileUrls, {});
    } finally {
      setAnalyzing(false);
    }
  };

  // Website flow: user pasted a site URL → crawl a sample + draft a schema, then
  // open the SAME confirm modal as file upload.
  const handleWebsiteSubmitted = async (url) => {
    if (!guardWrite()) return;
    setWebsiteOpen(false);
    setError(null);
    setWebsiteUrl(url);
    setDrafts(null);
    setAnalyzing(true);
    setAnalyzeOpen(true);
    try {
      const response = await fetch(`${API}analyze_product_website`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ bot_id: bot.id, website_url: url }),
      });
      if (!response.ok) {
        if (response.status === 403) { setAnalyzeOpen(false); return; }
        throw new Error(`analyze failed (${response.status})`);
      }
      const data = await response.json();
      setDrafts(data.catalogs || []);
    } catch (err) {
      console.error('Website analysis failed:', err);
      setError(err instanceof Error ? err.message : String(err));
      setAnalyzeOpen(false);
      setWebsiteUrl(null);
    } finally {
      setAnalyzing(false);
    }
  };

  // Step 2: owner confirmed → ingest with the confirmed schemas. Routes to the
  // website crawl endpoint when this was a website flow, else the file endpoint.
  const handleConfirmIngest = async (schemas) => {
    setAnalyzeOpen(false);
    if (websiteUrl) {
      const schema = (schemas && (schemas[websiteUrl] || Object.values(schemas)[0])) || {};
      await ingestWebsite(websiteUrl, schema);
    } else {
      await ingestProducts(pendingUrls, schemas);
    }
  };

  const ingestWebsite = async (url, schema) => {
    if (!guardWrite()) return;
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API}ingest_product_website`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          bot_id: bot.id, website_url: url, schema: schema || {},
          user_id: localStorage.getItem('user_id'),
        }),
      });
      if (!response.ok) {
        if (response.status === 403) return;
        throw new Error(`Failed to ingest website. Status: ${response.status}`);
      }
      await onRefresh();
    } catch (err) {
      console.error('Error ingesting website products:', err);
      setError(/Status: 403/.test(err.message) ? null : err.message);
    } finally {
      setLoading(false);
      setWebsiteUrl(null);
    }
  };

  const ingestProducts = async (fileUrls, schemas) => {
    if (!guardWrite()) return;
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API}ingest_products`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          bot_id: bot.id,
          product_urls: fileUrls,
          schemas: schemas || {},
          user_id: localStorage.getItem('user_id'),
        }),
      });
      if (!response.ok) {
        if (response.status === 403) return; // handled by the global permission modal
        throw new Error(`Failed to ingest products. Status: ${response.status}`);
      }
      await onRefresh();
    } catch (err) {
      console.error('Error ingesting products:', err);
      setError(/Status: 403/.test(err.message) ? null : err.message);
    } finally {
      setLoading(false);
      setPendingUrls([]);
    }
  };

  const requestDelete = (ids) => {
    if (!ids.length) return;
    setPendingDeleteIds(ids);
    setConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!guardWrite()) return;
    const ids = pendingDeleteIds;
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API}knowledge`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ bot_id: bot.id, ids }),
      });
      if (!response.ok) {
        if (response.status === 403) return;
        throw new Error(`Failed to delete products. Status: ${response.status}`);
      }
      setSelectedIds(prev => prev.filter(id => !ids.includes(id)));
      await onRefresh();
    } catch (err) {
      console.error('Error deleting products:', err);
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
      setPendingDeleteIds([]);
    }
  };

  const statusIcon = (status) => {
    if (status === 'scraping' || status === 'processing' || status === 'generating') {
      return <div className="knowledge-loading-spinner" />;
    }
    if (status === 'failed') {
      return <FontAwesomeIcon icon={faCircleXmark} className="knowledge-bad-status-icon" />;
    }
    return <FontAwesomeIcon icon={faCircleCheck} className="knowledge-okay-status-icon" />;
  };

  return (
    <>
      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
        <button
          className="knowledge-add-button"
          title={t('add_product')}
          onClick={() => isAtKnowledgeLimit ? onUpgrade?.() : setUploadOpen(true)}
        >
          <div className="knowledge-add-button-text">
            <FontAwesomeIcon icon={faPlus} /> {t('add_product')}
          </div>
        </button>
        {/* Crawling a website for products is URL training too: hidden for
            Webflow-managed agents (utils/webflowGate.js; the backend refuses
            it as well). */}
        {!hideWebsiteSource && (
        <button
          className="knowledge-add-button"
          title={t('add_product_from_website')}
          onClick={() => isAtKnowledgeLimit ? onUpgrade?.() : setWebsiteOpen(true)}
        >
          <div className="knowledge-add-button-text">
            <FontAwesomeIcon icon={faGlobe} /> {t('add_product_from_website')}
          </div>
        </button>
        )}
      </div>

      {error && (
        <div className="file-error-banner" style={{ marginTop: '10px' }}>
          <FontAwesomeIcon icon={faCircleXmark} style={{ color: '#dc0e0e', fontSize: 14, marginRight: 8 }} />
          <span>{error}</span>
        </div>
      )}

      <div className="knowledge-add-button-parent">
        <div className="knowledge-container">
          <div className="knowledge-header">
            <div className="header-title-with-search">
              <h3 className="header-title">{t('products')}</h3>
              <div className="knowledge-search-container">
                <FontAwesomeIcon icon={faMagnifyingGlass} className="knowledge-search-icon" />
                <input
                  type="text"
                  className="knowledge-search-input"
                  placeholder={t('search') || 'Search...'}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                {search && (
                  <button
                    type="button"
                    className="knowledge-search-clear"
                    onClick={() => setSearch('')}
                    aria-label="Clear search"
                  >
                    <FontAwesomeIcon icon={faXmark} />
                  </button>
                )}
              </div>
            </div>
            <div className="knowledge-bulk-actions" style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              <span style={{ color: '#6b7280' }}>{selectedIds.length} {t('selected')}</span>
              <button
                className="knowledge-action-button"
                onClick={() => requestDelete(selectedIds)}
                disabled={selectedIds.length === 0}
                title={t('remove')}
                style={{ opacity: selectedIds.length ? 1 : 0.5, cursor: selectedIds.length ? 'pointer' : 'not-allowed' }}
              >
                <FontAwesomeIcon icon={faTrash} />
              </button>
              <p style={{ marginLeft: '8px' }}>{t('count')}: {manualProducts.length}</p>
            </div>
          </div>

          {filtered.length > 0 ? (
            <div className="knowledge-content">
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px', fontSize: '15px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <Checkbox
                    checked={allSelected}
                    indeterminate={someSelected}
                    onChange={toggleSelectAll}
                    size="small"
                    sx={{ padding: 0, marginRight: '4px', color: '#5616ea', '&.Mui-checked': { color: '#5616ea' }, '&.MuiCheckbox-indeterminate': { color: '#5616ea' } }}
                  />
                  <span>{t('select_all')}</span>
                </label>
              </div>
              {filtered.map((item, index) => {
                const checked = selectedIds.includes(item.id);
                const disabledRow = item.status === 'scraping' || item.status === 'processing' || item.status === 'generating';
                return (
                  <React.Fragment key={item.id}>
                    <div className="knowledge-item">
                      <h3 className="knowledge-link" title={item.document_name}>
                        <div className="knowledge-status-link" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <Checkbox
                            checked={checked}
                            disabled={disabledRow}
                            onChange={() => toggleSelected(item.id)}
                            size="small"
                            sx={{ padding: 0, marginRight: '4px', color: '#5616ea', '&.Mui-checked': { color: '#5616ea' } }}
                          />
                          {statusIcon(item.status)}
                          {item.document_name || item.url_link}
                        </div>
                      </h3>
                      <div className="knowledge-date-action-container">
                        <div className="knowledge-actions" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <button
                            className="knowledge-action-button"
                            onClick={() => setPreviewItem(item)}
                            disabled={disabledRow}
                            title={t('view_data')}
                            style={disabledRow ? { opacity: 0.4, cursor: 'not-allowed' } : undefined}
                          >
                            <FontAwesomeIcon icon={faEye} />
                          </button>
                          <button
                            className="knowledge-action-button"
                            onClick={() => requestDelete([item.id])}
                            title={t('remove')}
                          >
                            <FontAwesomeIcon icon={faTrash} />
                          </button>
                        </div>
                      </div>
                    </div>
                    {index < filtered.length - 1 && <div className="knowledge-item-separator" />}
                  </React.Fragment>
                );
              })}
            </div>
          ) : (
            <div className="knowledge-content">
              <p style={{ color: '#6b7280', padding: '8px 0' }}>{t('products_empty')}</p>
            </div>
          )}
        </div>
      </div>

      <UploadFileModal
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        onSubmit={handleFilesUploaded}
        remainingSlots={remainingSlots}
        allowedExtensions={['csv', 'json', 'tsv', 'txt']}
        acceptAttr=".csv,.json,.tsv,.txt"
        fileTags={['.csv', '.json', '.tsv']}
      />

      <ProductAnalyzerModal
        open={analyzeOpen}
        loading={analyzing}
        catalogs={drafts}
        onClose={() => { setAnalyzeOpen(false); setDrafts(null); setPendingUrls([]); setWebsiteUrl(null); }}
        onConfirm={handleConfirmIngest}
      />

      <ProductsPreviewModal
        open={!!previewItem}
        onClose={() => setPreviewItem(null)}
        botId={bot.id}
        knowledgeId={previewItem?.id}
        filename={previewItem?.document_name || previewItem?.url_link}
      />

      <AddProductWebsiteModal
        open={websiteOpen}
        onClose={() => setWebsiteOpen(false)}
        onSubmit={handleWebsiteSubmitted}
      />

      <ConfirmationModal
        open={confirmOpen}
        onClose={() => { setConfirmOpen(false); setPendingDeleteIds([]); }}
        onConfirm={handleConfirmDelete}
        itemName={pendingDeleteIds.length > 1 ? `${pendingDeleteIds.length} ${t('selected')}` : t('products')}
      />
    </>
  );
};

export default ProductsKnowledge;
